import React, { useState, useEffect, useCallback, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  getOvertimeTimeline,
  exportOvertimesExcel,
} from "../../../api/reportsApi";
import "./UsersOvertimes.css";

registerLocale("ru", ru);

const toMonthStart = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const toMonthEnd = (date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

const formatDateParam = (date) => date.toLocaleDateString("en-CA");

const getMonthKey = (date) => date.getFullYear() * 12 + date.getMonth();

const formatDisplayDate = (dateString) => {
  if (!dateString) return "—";
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("ru-RU");
};

const formatHours = (hours) => {
  if (hours == null || Number.isNaN(Number(hours))) return "—";
  const value = Number(hours);
  const totalMinutes = Math.round(value * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return String(wholeHours);
  }

  return `${wholeHours} ч ${minutes} мин`;
};

const sumHours = (items) =>
  items.reduce((sum, item) => {
    const value = Number(item.hours);
    return Number.isNaN(value) ? sum : sum + value;
  }, 0);

const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
};

const buildGroupedData = (rows) => {
  const byEmployee = new Map();

  rows.forEach((row) => {
    const monthKey = row.date.slice(0, 7);
    if (!byEmployee.has(row.fio)) {
      byEmployee.set(row.fio, {
        fio: row.fio,
        department: row.department,
        group: row.group,
        months: new Map(),
      });
    }

    const employee = byEmployee.get(row.fio);
    if (!employee.months.has(monthKey)) {
      employee.months.set(monthKey, []);
    }
    employee.months.get(monthKey).push(row);
  });

  return [...byEmployee.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "ru"))
    .map(([, employee]) => {
      const months = [...employee.months.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([monthKey, monthRows]) => ({
          monthKey,
          label: formatMonthLabel(monthKey),
          totalHours: sumHours(monthRows),
          rows: [...monthRows].sort((a, b) => a.date.localeCompare(b.date)),
        }));

      return {
        fio: employee.fio,
        department: employee.department,
        group: employee.group,
        totalHours: sumHours(months.flatMap((month) => month.rows)),
        months,
      };
    });
};

const buildSelectOptions = (rows, field) => {
  const uniqueValues = [
    ...new Set(
      rows
        .map((row) => row[field])
        .filter((value) => value && value !== "—"),
    ),
  ];

  return uniqueValues
    .sort((a, b) => String(a).localeCompare(String(b), "ru"))
    .map((value) => ({ value, label: value }));
};

const filterRowsBySelections = (rows, selections, skipField = null) => {
  const fioSet = new Set((selections.fio || []).map((option) => option.value));
  const departmentSet = new Set(
    (selections.department || []).map((option) => option.value),
  );
  const groupSet = new Set(
    (selections.group || []).map((option) => option.value),
  );
  const projectSet = new Set(
    (selections.project || []).map((option) => option.value),
  );

  return rows.filter((row) => {
    if (skipField !== "fio" && fioSet.size > 0 && !fioSet.has(row.fio))
      return false;
    if (
      skipField !== "department" &&
      departmentSet.size > 0 &&
      !departmentSet.has(row.department)
    ) {
      return false;
    }
    if (skipField !== "group" && groupSet.size > 0 && !groupSet.has(row.group))
      return false;
    if (
      skipField !== "project" &&
      projectSet.size > 0 &&
      !projectSet.has(row.projectName)
    ) {
      return false;
    }
    return true;
  });
};

const UsersOvertimes = () => {
  const [monthFrom, setMonthFrom] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [monthTo, setMonthTo] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [timeline, setTimeline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFio, setSelectedFio] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isGroupedView, setIsGroupedView] = useState(true);
  const [expandedEmployees, setExpandedEmployees] = useState(() => new Set());
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  const isMultiMonthPeriod =
    getMonthKey(monthFrom) !== getMonthKey(monthTo);

  const periodParams = useMemo(
    () => ({
      dateFrom: formatDateParam(toMonthStart(monthFrom)),
      dateTo: formatDateParam(toMonthEnd(monthTo)),
    }),
    [monthFrom, monthTo],
  );

  const loadTimeline = useCallback(async () => {
    if (getMonthKey(monthFrom) > getMonthKey(monthTo)) {
      setError("Начальный месяц не может быть позже конечного");
      setTimeline(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getOvertimeTimeline(periodParams);
      setTimeline(data);
    } catch (loadError) {
      console.error("Ошибка при загрузке переработок:", loadError);
      setTimeline(null);
      setError(loadError.message || "Ошибка при загрузке переработок");
    } finally {
      setIsLoading(false);
    }
  }, [monthFrom, monthTo, periodParams]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const handleMonthFromChange = (date) => {
    const monthStart = toMonthStart(date);
    setMonthFrom(monthStart);
    if (monthTo && getMonthKey(monthStart) > getMonthKey(monthTo)) {
      setMonthTo(monthStart);
    }
  };

  const handleMonthToChange = (date) => {
    const monthStart = toMonthStart(date);
    setMonthTo(monthStart);
    if (monthFrom && getMonthKey(monthStart) < getMonthKey(monthFrom)) {
      setMonthFrom(monthStart);
    }
  };

  const handleExport = async () => {
    if (getMonthKey(monthFrom) > getMonthKey(monthTo)) {
      setError("Начальный месяц не может быть позже конечного");
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      await exportOvertimesExcel(periodParams.dateFrom, periodParams.dateTo);
    } catch (exportError) {
      console.error("Ошибка при выгрузке переработок:", exportError);
      setError(exportError.message || "Ошибка при выгрузке в Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const rows = useMemo(() => {
    const days = timeline?.days || [];
    return days.flatMap((day) =>
      (day.items || []).map((item, index) => ({
        key: `${day.date}-${item.user_id}-${item.project_id}-${index}`,
        date: day.date,
        fio: item.fio || "—",
        department: item.department || "—",
        group: item.group || "—",
        projectName: item.project_name || "—",
        hours: item.hours,
      })),
    );
  }, [timeline]);

  const selections = useMemo(
    () => ({
      fio: selectedFio,
      department: selectedDepartments,
      group: selectedGroups,
      project: selectedProjects,
    }),
    [selectedFio, selectedDepartments, selectedGroups, selectedProjects],
  );

  const fioOptions = useMemo(
    () =>
      buildSelectOptions(filterRowsBySelections(rows, selections, "fio"), "fio"),
    [rows, selections],
  );
  const departmentOptions = useMemo(
    () =>
      buildSelectOptions(
        filterRowsBySelections(rows, selections, "department"),
        "department",
      ),
    [rows, selections],
  );
  const groupOptions = useMemo(
    () =>
      buildSelectOptions(
        filterRowsBySelections(rows, selections, "group"),
        "group",
      ),
    [rows, selections],
  );
  const projectOptions = useMemo(
    () =>
      buildSelectOptions(
        filterRowsBySelections(rows, selections, "project"),
        "projectName",
      ),
    [rows, selections],
  );

  const filteredRows = useMemo(
    () => filterRowsBySelections(rows, selections),
    [rows, selections],
  );

  const groupedData = useMemo(
    () => buildGroupedData(filteredRows),
    [filteredRows],
  );

  const showGroupedView = isMultiMonthPeriod && isGroupedView;

  useEffect(() => {
    if (!showGroupedView) {
      return;
    }

    if (groupedData.length === 1) {
      const employee = groupedData[0];
      setExpandedEmployees(new Set([employee.fio]));
      setExpandedMonths(
        new Set(employee.months.map((month) => `${employee.fio}::${month.monthKey}`)),
      );
      return;
    }

    setExpandedEmployees(new Set());
    setExpandedMonths(new Set());
  }, [groupedData, showGroupedView]);

  const toggleEmployee = (fio) => {
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(fio)) {
        next.delete(fio);
        setExpandedMonths((monthState) => {
          const nextMonths = new Set(monthState);
          [...nextMonths].forEach((key) => {
            if (key.startsWith(`${fio}::`)) {
              nextMonths.delete(key);
            }
          });
          return nextMonths;
        });
      } else {
        next.add(fio);
      }
      return next;
    });
  };

  const toggleMonth = (fio, monthKey) => {
    const key = `${fio}::${monthKey}`;
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        setExpandedEmployees((employeeState) => new Set(employeeState).add(fio));
      }
      return next;
    });
  };

  useEffect(() => {
    const validFio = new Set(fioOptions.map((option) => option.value));
    const nextSelectedFio = selectedFio.filter((item) => validFio.has(item.value));
    if (nextSelectedFio.length !== selectedFio.length) {
      setSelectedFio(nextSelectedFio);
    }

    const validDepartments = new Set(
      departmentOptions.map((option) => option.value),
    );
    const nextSelectedDepartments = selectedDepartments.filter((item) =>
      validDepartments.has(item.value),
    );
    if (nextSelectedDepartments.length !== selectedDepartments.length) {
      setSelectedDepartments(nextSelectedDepartments);
    }

    const validGroups = new Set(groupOptions.map((option) => option.value));
    const nextSelectedGroups = selectedGroups.filter((item) =>
      validGroups.has(item.value),
    );
    if (nextSelectedGroups.length !== selectedGroups.length) {
      setSelectedGroups(nextSelectedGroups);
    }

    const validProjects = new Set(projectOptions.map((option) => option.value));
    const nextSelectedProjects = selectedProjects.filter((item) =>
      validProjects.has(item.value),
    );
    if (nextSelectedProjects.length !== selectedProjects.length) {
      setSelectedProjects(nextSelectedProjects);
    }
  }, [
    fioOptions,
    departmentOptions,
    groupOptions,
    projectOptions,
    selectedFio,
    selectedDepartments,
    selectedGroups,
    selectedProjects,
  ]);

  const hasData = filteredRows.length > 0;
  const filteredTotalHours = useMemo(
    () =>
      filteredRows.reduce((sum, row) => {
        const value = Number(row.hours);
        return Number.isNaN(value) ? sum : sum + value;
      }, 0),
    [filteredRows],
  );

  return (
    <div className="users-overtimes">
      <div className="users-overtimes__header">
        <h2 className="users-overtimes__title">Переработки пользователей</h2>
        <div className="users-overtimes__controls">
          <div className="users-overtimes__period">
            <span className="users-overtimes__period-label">Период</span>
            <DatePicker
              selected={monthFrom}
              onChange={handleMonthFromChange}
              dateFormat="MM.yyyy"
              showMonthYearPicker
              locale="ru"
              className="users-overtimes__datepicker"
              calendarClassName="users-overtimes-month-calendar"
              maxDate={monthTo || undefined}
            />
            <span className="users-overtimes__period-separator">—</span>
            <DatePicker
              selected={monthTo}
              onChange={handleMonthToChange}
              dateFormat="MM.yyyy"
              showMonthYearPicker
              locale="ru"
              className="users-overtimes__datepicker"
              calendarClassName="users-overtimes-month-calendar"
              minDate={monthFrom || undefined}
            />
          </div>
          <button
            type="button"
            className="users-overtimes__export-button"
            onClick={handleExport}
            disabled={isExporting || isLoading}
          >
            {isExporting ? "Выгрузка..." : "Выгрузить в Excel"}
          </button>
        </div>
      </div>

      {timeline && (
        <div className="users-overtimes__summary">
          <span>
            Период: {formatDisplayDate(timeline.date_from)} —{" "}
            {formatDisplayDate(timeline.date_to)}
          </span>
          <div className="users-overtimes__summary-actions">
            {isMultiMonthPeriod && (
              <div className="users-overtimes__view-toggle">
                <button
                  type="button"
                  className={`users-overtimes__view-toggle-button${
                    showGroupedView ? " users-overtimes__view-toggle-button--active" : ""
                  }`}
                  onClick={() => setIsGroupedView(true)}
                >
                  По сотрудникам
                </button>
                <button
                  type="button"
                  className={`users-overtimes__view-toggle-button${
                    !showGroupedView ? " users-overtimes__view-toggle-button--active" : ""
                  }`}
                  onClick={() => setIsGroupedView(false)}
                >
                  Список
                </button>
              </div>
            )}
            <span className="users-overtimes__summary-total">
              Итого: {formatHours(timeline.total_hours)} ч | Отфильтровано:{" "}
              {formatHours(filteredTotalHours)} ч
            </span>
          </div>
        </div>
      )}

      {error && <div className="users-overtimes__error">{error}</div>}

      <div className="users-overtimes__filters">
        <div className="users-overtimes__filter">
          <label>ФИО</label>
          <Select
            isMulti
            value={selectedFio}
            onChange={(value) => setSelectedFio(value || [])}
            options={fioOptions}
            classNamePrefix="users-overtimes-select"
            placeholder="Все"
            noOptionsMessage={() => "Нет значений"}
          />
        </div>
        <div className="users-overtimes__filter">
          <label>Отделение</label>
          <Select
            isMulti
            value={selectedDepartments}
            onChange={(value) => setSelectedDepartments(value || [])}
            options={departmentOptions}
            classNamePrefix="users-overtimes-select"
            placeholder="Все"
            noOptionsMessage={() => "Нет значений"}
          />
        </div>
        <div className="users-overtimes__filter">
          <label>Отдел</label>
          <Select
            isMulti
            value={selectedGroups}
            onChange={(value) => setSelectedGroups(value || [])}
            options={groupOptions}
            classNamePrefix="users-overtimes-select"
            placeholder="Все"
            noOptionsMessage={() => "Нет значений"}
          />
        </div>
        <div className="users-overtimes__filter">
          <label>Проект</label>
          <Select
            isMulti
            value={selectedProjects}
            onChange={(value) => setSelectedProjects(value || [])}
            options={projectOptions}
            classNamePrefix="users-overtimes-select"
            placeholder="Все"
            noOptionsMessage={() => "Нет значений"}
          />
        </div>
      </div>

      <div className="users-overtimes__table-container">
        {isLoading ? (
          <div className="users-overtimes__empty">Загрузка...</div>
        ) : hasData ? (
          <table className="users-overtimes__table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>ФИО</th>
                <th>Отделение</th>
                <th>Отдел</th>
                <th>Проект</th>
                <th>Часы</th>
              </tr>
            </thead>
            <tbody>
              {showGroupedView
                ? groupedData.map((employee) => {
                    const isEmployeeExpanded = expandedEmployees.has(employee.fio);

                    return (
                      <React.Fragment key={employee.fio}>
                        <tr
                          className="users-overtimes__group-row users-overtimes__group-row--employee"
                          onClick={() => toggleEmployee(employee.fio)}
                        >
                          <td colSpan={5}>
                            <button
                              type="button"
                              className="users-overtimes__group-toggle"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleEmployee(employee.fio);
                              }}
                            >
                              <span
                                className={`users-overtimes__group-chevron${
                                  isEmployeeExpanded
                                    ? " users-overtimes__group-chevron--expanded"
                                    : ""
                                }`}
                              >
                                ▶
                              </span>
                              <span className="users-overtimes__group-title">
                                {employee.fio}
                              </span>
                              <span className="users-overtimes__group-meta">
                                {employee.department} · {employee.group}
                              </span>
                            </button>
                          </td>
                          <td className="users-overtimes__group-total">
                            {formatHours(employee.totalHours)}
                          </td>
                        </tr>
                        {isEmployeeExpanded &&
                          employee.months.map((month) => {
                            const monthKey = `${employee.fio}::${month.monthKey}`;
                            const isMonthExpanded = expandedMonths.has(monthKey);

                            return (
                              <React.Fragment key={monthKey}>
                                <tr
                                  className="users-overtimes__group-row users-overtimes__group-row--month"
                                  onClick={() =>
                                    toggleMonth(employee.fio, month.monthKey)
                                  }
                                >
                                  <td colSpan={5}>
                                    <button
                                      type="button"
                                      className="users-overtimes__group-toggle users-overtimes__group-toggle--month"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        toggleMonth(employee.fio, month.monthKey);
                                      }}
                                    >
                                      <span
                                        className={`users-overtimes__group-chevron${
                                          isMonthExpanded
                                            ? " users-overtimes__group-chevron--expanded"
                                            : ""
                                        }`}
                                      >
                                        ▶
                                      </span>
                                      <span className="users-overtimes__group-title">
                                        {month.label}
                                      </span>
                                    </button>
                                  </td>
                                  <td className="users-overtimes__group-total">
                                    {formatHours(month.totalHours)}
                                  </td>
                                </tr>
                                {isMonthExpanded &&
                                  month.rows.map((row) => (
                                    <tr
                                      key={row.key}
                                      className="users-overtimes__detail-row"
                                    >
                                      <td>{formatDisplayDate(row.date)}</td>
                                      <td>{row.fio}</td>
                                      <td>{row.department}</td>
                                      <td>{row.group}</td>
                                      <td>{row.projectName}</td>
                                      <td>{formatHours(row.hours)}</td>
                                    </tr>
                                  ))}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })
                : filteredRows.map((row) => (
                    <tr key={row.key}>
                      <td>{formatDisplayDate(row.date)}</td>
                      <td>{row.fio}</td>
                      <td>{row.department}</td>
                      <td>{row.group}</td>
                      <td>{row.projectName}</td>
                      <td>{formatHours(row.hours)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        ) : (
          <div className="users-overtimes__empty">
            {timeline ? "Нет данных по выбранным фильтрам" : "Загрузка..."}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersOvertimes;
